import { useEffect, useState } from 'react';
import BrandSpinner from '@shape-construction/arch-ui/src/BrandSpinner';
import { useTranslation } from 'react-i18next';
// Side-effect import: this component is prerendered into index.html (root's
// HydrateFallback), where LocaleProvider hasn't run yet — without the
// initialized instance, t() would render raw keys into the static HTML.
import 'localization/i18n';
import './ShapeLoader.css';

export type ShapeLoaderProps = {
  /**
   * The full-screen treatment adds the cycling status messages and a larger
   * mark that makes one revolution as each message swaps in.
   */
  variant?: 'screen';
};

const MESSAGE_KEYS = [
  'loadingScreen.messages.settingUpSite',
  'loadingScreen.messages.readingBlueprints',
  'loadingScreen.messages.layingGroundwork',
  'loadingScreen.messages.raisingScaffolding',
  'loadingScreen.messages.tighteningBolts',
  'loadingScreen.messages.toppingOut',
] as const;

const MESSAGE_INTERVAL_MS = 2400;

export const ShapeLoader = ({ variant }: ShapeLoaderProps) => {
  const { t } = useTranslation();
  const [messageIndex, setMessageIndex] = useState(0);
  const isScreen = variant === 'screen';

  useEffect(() => {
    if (!isScreen) return;
    const interval = setInterval(() => {
      setMessageIndex((index) => (index + 1) % MESSAGE_KEYS.length);
    }, MESSAGE_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isScreen]);

  return (
    <output className="relative flex flex-col items-center gap-3" aria-label={t('state.loading')}>
      {/* The screen mark spins in step with the messages via the app-side
          class below; BrandSpinner's own continuous spin stays inline-only. */}
      <BrandSpinner
        size={isScreen ? 'xl' : 'lg'}
        spin={!isScreen}
        className={isScreen ? 'shape-loader-step-spin' : undefined}
      />
      {isScreen && (
        <p key={messageIndex} className="shape-loader-message text-xs text-neutral-subtle" aria-hidden="true">
          {t(MESSAGE_KEYS[messageIndex])}
        </p>
      )}
    </output>
  );
};
